import { useState, useEffect } from "react";
import { Icon, api, paymentCallbackUrl, payWithFlutterwaveInline, useAuth } from "../lib/core";

export const TxRow = ({ tx, getCategoryIcon, showBalance }) => {
  const isCredit = tx.type === "credit";
  const d = new Date(tx.timestamp);
  const today = new Date();
  const isToday = d.toDateString() === today.toDateString();
  const timeLabel = isToday
    ? `Today, ${d.toLocaleTimeString([], { hour: "numeric", minute: "2-digit" })}`
    : d.toLocaleDateString([], { month: "short", day: "numeric" });
  return (
    <div className="flex items-center gap-4 px-4 py-3.5">
      <div className={`w-10 h-10 rounded-full flex items-center justify-center shrink-0 ${isCredit ? "bg-green-100" : "bg-red-50"}`}>
        <Icon name={getCategoryIcon(tx.category)} className={isCredit ? "text-green-600" : "text-primary-custom"} />
      </div>
      <div className="flex-1 min-w-0">
        <p className="font-medium text-slate-900 truncate">{tx.description}</p>
        <p className="text-xs text-slate-400 capitalize">{(tx.category || "").replace(/_/g, " ")} · {timeLabel}</p>
      </div>
      <div className="text-right shrink-0">
        <p className={`font-bold ${isCredit ? "text-green-600" : "text-slate-900"}`}>
          {isCredit ? "+" : "-"}₦{tx.amount.toLocaleString()}
        </p>
        {showBalance && <p className="text-xs text-slate-400">Bal: ₦{tx.balance_after.toLocaleString()}</p>}
      </div>
    </div>
  );
};

// Wallet Page Component

export const WalletPage = ({ onBack }) => {
  const [wallet, setWallet] = useState(null);
  const [transactions, setTransactions] = useState([]);
  const [analytics, setAnalytics] = useState(null);
  const [loading, setLoading] = useState(true);
  const [view, setView] = useState("main");
  const [showBalance, setShowBalance] = useState(true);
  const [showFundModal, setShowFundModal] = useState(false);
  const [showReferralModal, setShowReferralModal] = useState(false);
  const [message, setMessage] = useState({ type: "", text: "" });

  useEffect(() => {
    fetchWalletData();
  }, []);

  const fetchWalletData = async () => {
    try {
      const [walletRes, txRes, analyticsRes] = await Promise.all([
        api.get("/wallet"),
        api.get("/wallet/transactions?limit=20"),
        api.get("/wallet/analytics")
      ]);
      setWallet(walletRes.data);
      setTransactions(txRes.data.transactions);
      setAnalytics(analyticsRes.data);
    } catch (err) {
      console.error("Failed to fetch wallet data:", err);
    } finally {
      setLoading(false);
    }
  };

  const getCategoryIcon = (category) => {
    const icons = {
      fund_add: "add_circle",
      recharge: "phone_android",
      commission: "trending_up",
      referral: "people",
      withdrawal: "swap_horiz",
      bonus: "card_giftcard"
    };
    return icons[category] || "receipt";
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background-light flex items-center justify-center">
        <Icon name="progress_activity" className="text-primary-custom text-4xl animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background-light">
      {/* Header */}
      <header className="bg-white border-b border-slate-100 sticky top-0 z-50">
        <div className="max-w-lg mx-auto px-4 py-4 flex items-center gap-3">
          <button onClick={onBack} data-testid="wallet-back-btn" className="p-2 hover:bg-slate-100 rounded-lg transition-colors text-slate-700">
            <Icon name="arrow_back" />
          </button>
          <span className="font-bold text-xl flex-1 text-slate-900">My Wallet</span>
          <button
            onClick={() => setShowReferralModal(true)}
            className="p-2 hover:bg-slate-100 rounded-lg transition-colors text-slate-600"
            data-testid="referral-btn"
          >
            <Icon name="share" />
          </button>
          <button
            onClick={() => { setLoading(true); fetchWalletData(); }}
            className="p-2 hover:bg-slate-100 rounded-lg transition-colors text-slate-600"
            data-testid="wallet-refresh-btn"
          >
            <Icon name="refresh" />
          </button>
        </div>
      </header>

      <main className="max-w-lg mx-auto px-4 py-6 space-y-6 pb-24">
        {/* Message */}
        {message.text && (
          <div className={`px-4 py-3 rounded-xl flex items-center gap-2 ${
            message.type === "success" 
              ? "bg-green-50 border border-green-200 text-green-700" 
              : "bg-red-50 border border-red-200 text-red-700"
          }`}>
            <Icon name={message.type === "success" ? "check_circle" : "error"} />
            <span>{message.text}</span>
            <button onClick={() => setMessage({ type: "", text: "" })} className="ml-auto">
              <Icon name="close" className="text-sm" />
            </button>
          </div>
        )}

        {/* Total Balance Card */}
        <div className="relative overflow-hidden bg-gradient-to-br from-primary-custom to-red-700 rounded-3xl p-6 text-white shadow-lg shadow-red-900/20">
          {/* Watermark logo */}
          <Icon name="trending_up" className="absolute -right-4 -bottom-4 text-white/10 select-none pointer-events-none" style={{ fontSize: "9rem" }} />
          <div className="relative">
            <p className="text-white/70 text-sm">Total Wallet Balance</p>
            <div className="flex items-center gap-3 mt-1">
              <p className="text-4xl font-extrabold tracking-tight" data-testid="total-balance">
                {showBalance ? `₦${(wallet?.total_balance || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}` : "₦ ••••••"}
              </p>
              <button onClick={() => setShowBalance(!showBalance)} data-testid="toggle-balance-btn" className="p-1.5 hover:bg-white/10 rounded-lg">
                <Icon name={showBalance ? "visibility" : "visibility_off"} className="text-white/80 text-lg" />
              </button>
            </div>
            {analytics && (
              <div className="inline-flex items-center gap-1 mt-3 bg-white/15 rounded-full px-2.5 py-1 text-xs font-semibold">
                <Icon name={analytics.spend_trend_pct >= 0 ? "trending_up" : "trending_down"} className="text-sm" />
                <span data-testid="spend-trend">{Math.abs(analytics.spend_trend_pct || 0)}% vs last month</span>
              </div>
            )}

            {/* Quick Actions */}
            <div className="grid grid-cols-4 gap-2 mt-6">
              {[
                { icon: "account_balance_wallet", label: "Add Money", testid: "qa-add-money", onClick: () => setShowFundModal(true) },
                { icon: "north_east", label: "Top Up", testid: "qa-topup", onClick: onBack },
                { icon: "receipt_long", label: "History", testid: "qa-history", onClick: () => setView("transactions") },
              ].map((a) => (
                <button key={a.label} onClick={a.onClick} data-testid={a.testid} className="flex flex-col items-center gap-1.5 group">
                  <span className="w-12 h-12 rounded-2xl bg-white/15 group-hover:bg-white/25 transition-colors flex items-center justify-center">
                    <Icon name={a.icon} className="text-white text-xl" />
                  </span>
                  <span className="text-[11px] text-white/90 font-medium">{a.label}</span>
                </button>
              ))}
            </div>
          </div>
        </div>

        {/* Balance Breakdown */}
        <div className="grid grid-cols-3 gap-3">
          <div className="bg-white rounded-2xl p-3 text-center border border-slate-100">
            <p className="text-slate-400 text-xs">Main</p>
            <p className="font-bold text-slate-900 mt-0.5" data-testid="main-balance">₦{(wallet?.balance || 0).toLocaleString()}</p>
          </div>
          <div className="bg-white rounded-2xl p-3 text-center border border-slate-100">
            <p className="text-slate-400 text-xs">Bonus</p>
            <p className="font-bold text-amber-500 mt-0.5" data-testid="bonus-balance">₦{(wallet?.bonus_balance || 0).toLocaleString()}</p>
          </div>
          <div className="bg-white rounded-2xl p-3 text-center border border-slate-100">
            <p className="text-slate-400 text-xs">Cashback</p>
            <p className="font-bold text-green-600 mt-0.5" data-testid="commission-balance">₦{(wallet?.commission_balance || 0).toLocaleString()}</p>
          </div>
        </div>

        {/* ===== MAIN VIEW ===== */}
        {view === "main" && (
          <>
            {/* Referral Earnings */}
            <div>
              <div className="flex items-center justify-between mb-3">
                <h3 className="font-bold text-slate-900">Referral Earnings</h3>
                <button onClick={() => setShowReferralModal(true)} data-testid="referral-see-all" className="text-sm font-semibold text-primary-custom flex items-center gap-0.5">
                  See all <Icon name="chevron_right" className="text-base" />
                </button>
              </div>
              <div className="grid grid-cols-1 gap-3">
                <div className="bg-white rounded-2xl p-4 border border-slate-100 flex items-center gap-4" data-testid="referral-lifetime-card">
                  <div className="w-11 h-11 rounded-xl bg-primary-custom/10 flex items-center justify-center shrink-0">
                    <Icon name="star" className="text-primary-custom text-xl" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-[11px] font-bold text-slate-400 tracking-wide uppercase">Lifetime Earned</p>
                    <p className="text-sm text-slate-500">Total from all referrals</p>
                  </div>
                  <div className="text-right">
                    <p className="font-bold text-slate-900">₦{(analytics?.total_referral_earned_lifetime || 0).toLocaleString()}</p>
                    {analytics?.referral_earned_today > 0 && (
                      <span className="inline-block mt-1 text-[11px] font-semibold text-green-700 bg-green-100 rounded-full px-2 py-0.5">+₦{analytics.referral_earned_today.toLocaleString()} today</span>
                    )}
                  </div>
                </div>
                <div className="bg-white rounded-2xl p-4 border border-slate-100 flex items-center gap-4" data-testid="referral-pending-card">
                  <div className="w-11 h-11 rounded-xl bg-amber-100 flex items-center justify-center shrink-0">
                    <Icon name="schedule" className="text-amber-500 text-xl" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-[11px] font-bold text-slate-400 tracking-wide uppercase">Pending Rewards</p>
                    <p className="text-sm text-slate-500">Awaiting {analytics?.pending_referral_count || 0} friend top-up{(analytics?.pending_referral_count || 0) === 1 ? "" : "s"}</p>
                  </div>
                  <p className="font-bold text-slate-900">₦{(analytics?.pending_referral_reward || 0).toLocaleString()}</p>
                </div>
              </div>
            </div>

            {/* Cashback Rewards */}
            <div>
              <div className="flex items-center justify-between mb-3">
                <h3 className="font-bold text-slate-900">Cashback Rewards</h3>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div className="bg-white rounded-2xl p-4 border border-slate-100" data-testid="cashback-month-card">
                  <div className="w-10 h-10 rounded-xl bg-primary-custom/10 flex items-center justify-center mb-3">
                    <Icon name="percent" className="text-primary-custom" />
                  </div>
                  <p className="text-[11px] font-bold text-slate-400 tracking-wide uppercase">This Month</p>
                  <p className="text-xs text-slate-500">From your own top-ups</p>
                  <p className="text-xl font-bold text-slate-900 mt-2">₦{(analytics?.cashback_earned_this_month || 0).toLocaleString()}</p>
                  <p className="text-xs text-slate-400 mt-0.5">{analytics?.cashback_topup_count_this_month || 0} top-ups</p>
                </div>
                <div className="bg-white rounded-2xl p-4 border border-slate-100" data-testid="cashback-rate-card">
                  <div className="w-10 h-10 rounded-xl bg-amber-100 flex items-center justify-center mb-3">
                    <Icon name="bolt" className="text-amber-500" />
                  </div>
                  <p className="text-[11px] font-bold text-slate-400 tracking-wide uppercase">Cashback Rate</p>
                  <p className="text-xs text-slate-500">Standard tier</p>
                  <p className="text-xl font-bold text-slate-900 mt-2">{analytics?.cashback_rate_pct || 0}%</p>
                  <p className="text-xs text-slate-400 mt-0.5">on data top-ups</p>
                </div>
              </div>
            </div>

            {/* 7-Day Spending Trend */}
            {analytics?.spending_trend?.length > 0 && (
              <div className="bg-white rounded-2xl p-4 border border-slate-100">
                <p className="font-bold text-slate-900 mb-4">7-Day Spending Trend</p>
                <div className="flex items-end justify-between gap-2 h-28">
                  {analytics.spending_trend.map((day, i) => {
                    const maxAmount = Math.max(...analytics.spending_trend.map(d => d.amount), 1);
                    const height = (day.amount / maxAmount) * 100;
                    return (
                      <div key={i} className="flex-1 flex flex-col items-center gap-2">
                        <div className="w-full bg-primary-custom/15 rounded-t-lg relative" style={{ height: `${Math.max(height, 5)}%` }}>
                          <div className="absolute bottom-0 left-0 right-0 bg-primary-custom rounded-t-lg" style={{ height: `100%` }} />
                        </div>
                        <span className="text-xs text-slate-500">{day.day}</span>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Recent Transactions */}
            <div>
              <div className="flex items-center justify-between mb-3">
                <h3 className="font-bold text-slate-900">Recent Transactions</h3>
                <button onClick={() => setView("transactions")} data-testid="tx-view-all" className="text-sm font-semibold text-primary-custom flex items-center gap-0.5">
                  View all <Icon name="chevron_right" className="text-base" />
                </button>
              </div>
              {transactions.length === 0 ? (
                <div className="bg-white rounded-2xl border border-slate-100 text-center py-12 text-slate-400">
                  <Icon name="receipt_long" className="text-5xl" />
                  <p className="mt-2">No transactions yet</p>
                </div>
              ) : (
                <div className="bg-white rounded-2xl border border-slate-100 divide-y divide-slate-50">
                  {transactions.slice(0, 5).map((tx) => (
                    <TxRow key={tx.id} tx={tx} getCategoryIcon={getCategoryIcon} />
                  ))}
                </div>
              )}
            </div>
          </>
        )}

        {/* ===== TRANSACTIONS VIEW ===== */}
        {view === "transactions" && (
          <div>
            <button onClick={() => setView("main")} data-testid="tx-back-to-wallet" className="flex items-center gap-1 text-sm font-semibold text-slate-600 mb-4">
              <Icon name="arrow_back" className="text-base" /> Back to wallet
            </button>
            <h3 className="font-bold text-slate-900 mb-3">All Transactions</h3>
            {transactions.length === 0 ? (
              <div className="bg-white rounded-2xl border border-slate-100 text-center py-12 text-slate-400">
                <Icon name="receipt_long" className="text-5xl" />
                <p className="mt-2">No transactions yet</p>
              </div>
            ) : (
              <div className="bg-white rounded-2xl border border-slate-100 divide-y divide-slate-50">
                {transactions.map((tx) => (
                  <TxRow key={tx.id} tx={tx} getCategoryIcon={getCategoryIcon} showBalance />
                ))}
              </div>
            )}
          </div>
        )}
      </main>

      {/* Fund Wallet Modal */}
      {showFundModal && (
        <FundWalletModal
          onClose={() => setShowFundModal(false)}
          onSuccess={() => {
            setShowFundModal(false);
            fetchWalletData();
            setMessage({ type: "success", text: "Wallet funded successfully!" });
          }}
        />
      )}

      {/* Referral Modal */}
      {showReferralModal && wallet && (
        <ReferralModal
          wallet={wallet}
          onClose={() => setShowReferralModal(false)}
          onSuccess={() => {
            setShowReferralModal(false);
            fetchWalletData();
            setMessage({ type: "success", text: "Referral code applied!" });
          }}
        />
      )}
    </div>
  );
};

// Fund Wallet Modal Component

export const FundWalletModal = ({ onClose, onSuccess }) => {
  const { user } = useAuth();
  const [amount, setAmount] = useState("");
  const [loading, setLoading] = useState(false);
  const [paymentLink, setPaymentLink] = useState(null);
  const [verifying, setVerifying] = useState(false);
  const [txRef, setTxRef] = useState(null);
  const [flwConfig, setFlwConfig] = useState({ is_configured: false });

  const quickAmounts = [500, 1000, 2000, 5000, 10000, 20000];

  // Check if Flutterwave is configured
  useEffect(() => {
    const checkConfig = async () => {
      try {
        const res = await api.get("/payments/flutterwave/config");
        setFlwConfig(res.data);
      } catch (err) {
        console.log("Flutterwave not configured");
      }
    };
    checkConfig();
  }, []);

  const launchInline = async (ref, amt) => {
    try {
      await payWithFlutterwaveInline({
        txRef: ref,
        amount: amt,
        customer: { email: user?.email, phone: user?.phone, name: user?.full_name },
        title: "TopIt Wallet Top-Up",
        description: `Add ₦${Number(amt).toLocaleString()} to your wallet`,
        onSuccess: () => { verifyPayment(); },
        onClose: () => { /* keep the confirm panel open for manual verify */ },
      });
    } catch (e) {
      alert(e.message || "Could not open payment. Please try again.");
    }
  };


  const handleFund = async () => {
    if (!amount || parseFloat(amount) <= 0) return;

    setLoading(true);
    try {
      if (flwConfig.is_configured) {
        // Use Flutterwave (secure, server-verified)
        const res = await api.post("/payments/flutterwave/initiate", {
          amount: parseFloat(amount),
          redirect_url: paymentCallbackUrl()
        });
        
        if (res.data.success) {
          setTxRef(res.data.tx_ref);
          setPaymentLink(true);
          launchInline(res.data.tx_ref, parseFloat(amount));
        }
      } else {
        alert("Payment is temporarily unavailable. Please try again later.");
      }
    } catch (err) {
      alert(err.response?.data?.detail || "Failed to initiate payment");
    } finally {
      setLoading(false);
    }
  };

  const verifyPayment = async () => {
    if (!txRef) return;
    
    setVerifying(true);
    try {
      const res = await api.get(`/payments/flutterwave/verify/${txRef}`);
      
      if (res.data.success && res.data.status === "successful") {
        onSuccess();
      } else if (res.data.status === "failed") {
        alert("Payment failed. Please try again.");
        setPaymentLink(null);
        setTxRef(null);
      } else {
        alert("Payment is still processing. Please wait a moment and try again.");
      }
    } catch (err) {
      alert("Could not verify payment. Please try again.");
    } finally {
      setVerifying(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-end sm:items-center justify-center p-4">
      <div className="bg-white rounded-t-3xl sm:rounded-2xl w-full max-w-md">
        <div className="p-6 border-b border-slate-100 flex items-center justify-between">
          <h2 className="font-bold text-xl text-slate-900">Add Funds</h2>
          <button onClick={onClose} className="p-2 hover:bg-slate-100 rounded-lg">
            <Icon name="close" />
          </button>
        </div>
        
        <div className="p-6 space-y-4">
          {!paymentLink ? (
            <>
              <div>
                <label className="text-slate-700 text-sm font-semibold">Amount</label>
                <input
                  type="number"
                  value={amount}
                  onChange={(e) => setAmount(e.target.value)}
                  placeholder="Enter amount"
                  data-testid="fund-amount-input"
                  className="w-full h-14 px-4 mt-1.5 rounded-xl border border-slate-200 focus:border-primary-custom outline-none text-2xl font-bold text-center"
                />
              </div>

              <div className="grid grid-cols-3 gap-2">
                {quickAmounts.map((amt) => (
                  <button
                    key={amt}
                    onClick={() => setAmount(amt.toString())}
                    className={`py-3 rounded-xl font-medium transition-all ${
                      amount === amt.toString()
                        ? "bg-primary-custom text-white"
                        : "bg-slate-100 text-slate-700 hover:bg-slate-200"
                    }`}
                  >
                    ₦{amt.toLocaleString()}
                  </button>
                ))}
              </div>

              {flwConfig.is_configured ? (
                <div className="bg-green-50 border border-green-200 rounded-xl p-4 flex items-start gap-3">
                  <Icon name="verified" className="text-green-600 mt-0.5" />
                  <div>
                    <p className="text-sm text-green-800 font-medium">Secure Payment</p>
                    <p className="text-xs text-green-700 mt-1">Pay with Card, Bank Transfer, or USSD via Flutterwave</p>
                  </div>
                </div>
              ) : (
                <div className="bg-yellow-50 border border-yellow-200 rounded-xl p-4 flex items-start gap-3">
                  <Icon name="info" className="text-yellow-600 mt-0.5" />
                  <div>
                    <p className="text-sm text-yellow-800 font-medium">Demo Mode</p>
                    <p className="text-xs text-yellow-700 mt-1">Payment gateway not configured. Using simulated funding.</p>
                  </div>
                </div>
              )}

              <button
                onClick={handleFund}
                disabled={loading || !amount || parseFloat(amount) < 100}
                data-testid="confirm-fund-btn"
                className="w-full h-14 bg-primary-custom hover:bg-primary-custom/90 text-white rounded-xl font-bold text-lg flex items-center justify-center gap-2 disabled:opacity-50"
              >
                {loading ? <Icon name="progress_activity" className="animate-spin" /> : <Icon name="payments" />}
                <span>{flwConfig.is_configured ? "Pay" : "Add"} ₦{amount ? parseFloat(amount).toLocaleString() : "0"}</span>
              </button>
              
              {parseFloat(amount) > 0 && parseFloat(amount) < 100 && (
                <p className="text-center text-sm text-red-500">Minimum amount is ₦100</p>
              )}
            </>
          ) : (
            <div className="text-center space-y-4 py-4">
              <div className="w-20 h-20 bg-primary-custom/10 rounded-full flex items-center justify-center mx-auto">
                <Icon name="payments" className="text-4xl text-primary-custom" />
              </div>
              
              <div>
                <h3 className="font-bold text-lg text-slate-900">Complete Your Payment</h3>
                <p className="text-slate-500 mt-1">A payment window has opened. Complete your payment there.</p>
              </div>
              
              <div className="bg-slate-50 rounded-xl p-4">
                <p className="text-sm text-slate-600">Amount: <span className="font-bold text-slate-900">₦{parseFloat(amount).toLocaleString()}</span></p>
                <p className="text-xs text-slate-500 mt-1">Reference: {txRef}</p>
              </div>
              
              <div className="space-y-2">
                <button
                  onClick={verifyPayment}
                  disabled={verifying}
                  data-testid="wallet-fund-verify-btn"
                  className="w-full h-12 bg-primary-custom hover:bg-primary-custom/90 text-white rounded-xl font-bold flex items-center justify-center gap-2"
                >
                  {verifying ? <Icon name="progress_activity" className="animate-spin" /> : <Icon name="check_circle" />}
                  <span>I've Completed Payment</span>
                </button>
                
                <button
                  onClick={() => launchInline(txRef, parseFloat(amount))}
                  data-testid="wallet-fund-reopen-btn"
                  className="w-full h-12 bg-slate-100 hover:bg-slate-200 text-slate-700 rounded-xl font-medium flex items-center justify-center gap-2"
                >
                  <Icon name="open_in_new" />
                  <span>Reopen Payment</span>
                </button>
                
                <button
                  onClick={() => {
                    setPaymentLink(null);
                    setTxRef(null);
                  }}
                  className="w-full h-10 text-slate-500 hover:text-slate-700 font-medium"
                >
                  Cancel & Start Over
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

// Referral Modal Component

export const ReferralModal = ({ wallet, onClose, onSuccess }) => {
  const [referralCode, setReferralCode] = useState("");
  const [loading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState("share");

  const handleApplyReferral = async () => {
    if (!referralCode) return;

    setLoading(true);
    try {
      await api.post("/wallet/apply-referral", { referral_code: referralCode });
      onSuccess();
    } catch (err) {
      alert(err.response?.data?.detail || "Failed to apply referral code");
    } finally {
      setLoading(false);
    }
  };

  const shareMessage = `Join TOP-IT and get ₦500 bonus! Use my referral code: ${wallet.referral_code}`;

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-end sm:items-center justify-center p-4">
      <div className="bg-white rounded-t-3xl sm:rounded-2xl w-full max-w-md">
        <div className="p-6 border-b border-slate-100 flex items-center justify-between">
          <h2 className="font-bold text-xl text-slate-900">Referral Program</h2>
          <button onClick={onClose} className="p-2 hover:bg-slate-100 rounded-lg">
            <Icon name="close" />
          </button>
        </div>
        
        <div className="p-6 space-y-4">
          {/* Tabs */}
          <div className="flex gap-2">
            <button
              onClick={() => setActiveTab("share")}
              className={`flex-1 py-2.5 rounded-xl font-medium ${
                activeTab === "share" ? "bg-primary-custom text-white" : "bg-slate-100 text-slate-700"
              }`}
            >
              Share & Earn
            </button>
            <button
              onClick={() => setActiveTab("apply")}
              className={`flex-1 py-2.5 rounded-xl font-medium ${
                activeTab === "apply" ? "bg-primary-custom text-white" : "bg-slate-100 text-slate-700"
              }`}
            >
              Enter Code
            </button>
          </div>

          {activeTab === "share" ? (
            <>
              <div className="text-center py-4">
                <Icon name="card_giftcard" className="text-primary-custom text-5xl" />
                <h3 className="font-bold text-xl mt-3">Earn ₦500 per referral!</h3>
                <p className="text-slate-500 text-sm mt-2">Share your code and both you and your friend get ₦500 bonus</p>
              </div>

              <div className="bg-primary-custom/10 rounded-xl p-4 text-center">
                <p className="text-sm text-slate-600">Your Referral Code</p>
                <p className="text-3xl font-bold text-primary-custom font-mono mt-1">{wallet.referral_code}</p>
              </div>

              <div className="flex gap-3">
                <button
                  onClick={() => {
                    navigator.clipboard.writeText(wallet.referral_code);
                    alert("Code copied!");
                  }}
                  className="flex-1 py-3 bg-slate-100 rounded-xl font-medium flex items-center justify-center gap-2"
                >
                  <Icon name="content_copy" />
                  <span>Copy</span>
                </button>
                <button
                  onClick={() => {
                    if (navigator.share) {
                      navigator.share({ text: shareMessage });
                    } else {
                      navigator.clipboard.writeText(shareMessage);
                      alert("Message copied!");
                    }
                  }}
                  className="flex-1 py-3 bg-primary-custom text-white rounded-xl font-medium flex items-center justify-center gap-2"
                >
                  <Icon name="share" />
                  <span>Share</span>
                </button>
              </div>

              <div className="bg-slate-50 rounded-xl p-4">
                <div className="flex items-center gap-3">
                  <Icon name="people" className="text-primary-custom text-2xl" />
                  <div>
                    <p className="font-bold text-slate-900">{wallet.total_referrals} Referrals</p>
                    <p className="text-sm text-slate-500">₦{(wallet.total_referrals * 500).toLocaleString()} earned</p>
                  </div>
                </div>
              </div>
            </>
          ) : (
            <>
              <div className="text-center py-4">
                <Icon name="redeem" className="text-primary-custom text-5xl" />
                <h3 className="font-bold text-xl mt-3">Have a referral code?</h3>
                <p className="text-slate-500 text-sm mt-2">Enter it below to get your ₦500 bonus!</p>
              </div>

              <input
                type="text"
                value={referralCode}
                onChange={(e) => setReferralCode(e.target.value.toUpperCase())}
                placeholder="Enter referral code"
                data-testid="referral-code-input"
                className="w-full h-14 px-4 rounded-xl border border-slate-200 focus:border-primary-custom outline-none text-xl font-bold text-center font-mono tracking-wider"
                maxLength={10}
              />

              {wallet.referred_by && (
                <div className="bg-green-50 border border-green-200 rounded-xl p-4 flex items-center gap-3">
                  <Icon name="check_circle" className="text-green-600" />
                  <p className="text-sm text-green-700">You've already used a referral code</p>
                </div>
              )}

              <button
                onClick={handleApplyReferral}
                disabled={loading || !referralCode || wallet.referred_by}
                data-testid="apply-referral-btn"
                className="w-full h-14 bg-primary-custom hover:bg-primary-custom/90 text-white rounded-xl font-bold text-lg flex items-center justify-center gap-2 disabled:opacity-50"
              >
                {loading ? <Icon name="progress_activity" className="animate-spin" /> : <Icon name="redeem" />}
                <span>Apply Code</span>
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

// Scheduled Recharges Page Component
